import { IPaymentSpeed } from "models/HaulPay";

// The relationship's selected speed arrives as the display value of
// FactoringPaymentProfileRelation.PAYOUT_TERM_CHOICES — "ach_1_day", "ach_7_day", "ach_15_day",
// "ach_21_day", "ach_30_day" — while each payment_speed_options entry carries payout_days as a
// number. The 1 day term is stored as 0, so "ach_1_day" matches an option of 0 or 1.
const PAYOUT_DAYS_PATTERN = /ach_(\d+)_day/;

export const parsePayoutDays = (payoutDays?: string | null): number | null => {
  const match = PAYOUT_DAYS_PATTERN.exec(String(payoutDays ?? ""));
  return match ? parseInt(match[1], 10) : null;
};

export const findPaymentSpeedOption = (
  payoutDays?: string | null,
  options?: IPaymentSpeed[] | null
): IPaymentSpeed | undefined => {
  const parsed = parsePayoutDays(payoutDays);
  if (parsed === null) return undefined;

  return (options ?? []).find((option) =>
    parsed === 1 || parsed === 0
      ? option?.payout_days === 0 || option?.payout_days === 1
      : option?.payout_days === parsed
  );
};

// Matches the wording getPaymentSpeedLabel already produces elsewhere in the app, so a carrier sees
// the same phrasing on their connections as on a funding request.
export const buildPaymentSpeedLabel = (option?: IPaymentSpeed | null): string | undefined => {
  if (!option) return undefined;

  const days =
    option.payout_days === 0 || option.payout_days === 1 ? "1 Day" : `${option.payout_days} Days`;
  const rate = option.carrier_rate ?? 0;

  return rate === 0 ? `${days} (Free)` : `${days} (${rate}% Quick Pay Fee)`;
};

/**
 * Label for a broker connection's agreed payment speed, resolved from the relationship's own
 * payout_days rather than whichever option the broker happens to have flagged as default.
 */
export const getConnectedBrokerPaymentSpeedLabel = (
  payoutDays?: string | null,
  options?: IPaymentSpeed[] | null
): string | undefined => buildPaymentSpeedLabel(findPaymentSpeedOption(payoutDays, options));
